import { useState, useEffect } from 'react';
import { Shield, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, List } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PhotoCapture } from './PhotoCapture';
import { withPhotos, type PhotoGeoMap } from '../lib/photoLocation';
import { PHOTO_BUCKETS } from '../lib/photoUpload';
import { InspectionProgressBar, InspectionQuickNav, ItemStatus } from './InspectionProgressBar';
import { logEvent } from '../lib/telemetry';

interface WorkCodeTranslation {
  work_code_id: string;
  locale: string;
  translated_code: string;
  translated_description: string;
}

interface DoubleCheckFailureCode {
  id?: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface DoubleCheck {
  id: string;
  description: string;
  size: number;
  photo_url: string | null;
}

interface DoubleCheckInspection {
  doublecheck_id: string;
  failure_codes: string[];
  notes: string;
  photo_urls: string[];
  /** Where each photo was taken, keyed by its URL (or offline marker). */
  photo_geo?: PhotoGeoMap;
}

interface DoubleCheckInspectionFormProps {
  doubleChecks: DoubleCheck[];
  inspections: DoubleCheckInspection[];
  failureCodes: DoubleCheckFailureCode[];
  onInspectionChange: (inspections: DoubleCheckInspection[]) => void;
  expandedDoubleChecks?: Set<string>;
  onExpandedChange?: (expanded: Set<string>) => void;
  translations?: WorkCodeTranslation[];
  localDraftId?: string;
}

export function DoubleCheckInspectionForm({
  doubleChecks,
  inspections,
  failureCodes,
  onInspectionChange,
  translations = [],
  localDraftId,
}: DoubleCheckInspectionFormProps) {
  const { t } = useTranslation('inspection');
  const [localInspections, setLocalInspections] = useState<DoubleCheckInspection[]>(inspections);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showQuickNav, setShowQuickNav] = useState(false);

  useEffect(() => {
    setLocalInspections(prev => {
      return doubleChecks.map(dc => {
        const existing = prev.find(i => i.doublecheck_id === dc.id);
        if (existing) return existing;
        const defaultCode = failureCodes.find(fc => fc.is_default);
        return {
          doublecheck_id: dc.id,
          failure_codes: defaultCode ? [defaultCode.code] : [],
          notes: '',
          photo_urls: [],
        };
      });
    });
  }, [doubleChecks, failureCodes]);

  const getStatus = (dcId: string): ItemStatus => {
    const inspection = localInspections.find(i => i.doublecheck_id === dcId);
    if (!inspection || inspection.failure_codes.length === 0) return 'pending';
    const hasIssue = inspection.failure_codes.some(code => {
      const fc = failureCodes.find(f => f.code === code);
      return fc?.is_issue;
    });
    return hasIssue ? 'issues' : 'good';
  };

  const handleFailureCodeToggle = (dcId: string, code: string) => {
    const dc = doubleChecks.find(d => d.id === dcId);
    const clicked = failureCodes.find(fc => fc.code === code);
    const existingCodes = localInspections.find(i => i.doublecheck_id === dcId)?.failure_codes ?? [];
    const willRemove = existingCodes.includes(code);
    void logEvent('inspection_failure_code_toggled', `${willRemove ? '-' : '+'} ${code}`, {
      metadata: {
        device_type: 'doublecheck',
        device_id: dcId,
        doublecheck_description: dc?.description ?? null,
        doublecheck_size: dc?.size ?? null,
        code,
        code_is_issue: clicked?.is_issue ?? null,
        action: willRemove ? 'removed' : 'added',
      },
    });
    const updated = localInspections.map(inspection => {
      if (inspection.doublecheck_id !== dcId) return inspection;
      const hasCode = inspection.failure_codes.includes(code);
      if (!clicked) return inspection;

      let newCodes: string[];
      if (hasCode) {
        newCodes = inspection.failure_codes.filter(c => c !== code);
      } else if (clicked.is_issue) {
        newCodes = [...inspection.failure_codes.filter(c => {
          const fc = failureCodes.find(f => f.code === c);
          return fc?.is_issue;
        }), code];
      } else {
        newCodes = [code];
      }

      return { ...inspection, failure_codes: newCodes };
    });
    setLocalInspections(updated);
    onInspectionChange(updated);
  };

  const handleNotesChange = (dcId: string, notes: string) => {
    const updated = localInspections.map(inspection =>
      inspection.doublecheck_id === dcId ? { ...inspection, notes } : inspection
    );
    setLocalInspections(updated);
    onInspectionChange(updated);
  };

  /** Urls and locations in ONE update — sent separately, the second clobbered
   *  the first and the location was lost before saving (fa03402b). */
  const handlePhotosChange = (dcId: string, urls: string[], geo?: PhotoGeoMap) => {
    const updated = localInspections.map(inspection =>
      inspection.doublecheck_id === dcId ? withPhotos(inspection, urls, geo) : inspection
    );
    setLocalInspections(updated);
    onInspectionChange(updated);
  };



  const goNext = () => {
    if (currentIndex < doubleChecks.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  if (doubleChecks.length === 0) {
    return (
      <div className="bg-surface rounded-xl shadow-sm border border-line p-8 text-center">
        <Shield size={48} className="mx-auto text-theme-secondary mb-4" />
        <h3 className="text-lg font-semibold text-theme-secondary mb-2">{t('dcForm.noDoubleChecks')}</h3>
        <p className="text-theme-secondary">{t('dcForm.noDoubleChecksHint')}</p>
      </div>
    );
  }

  const dc = doubleChecks[currentIndex];
  const inspection = localInspections.find(i => i.doublecheck_id === dc.id);
  const status = getStatus(dc.id);

  const progressItems = doubleChecks.map(d => ({
    id: d.id,
    label: `${d.size}" - ${d.description}`,
    status: getStatus(d.id),
  }));

  const getTranslatedCode = (fc: DoubleCheckFailureCode) => {
    if (!fc.id || translations.length === 0) return fc.code;
    const tr = translations.find(x => x.work_code_id === fc.id);
    return tr?.translated_code || fc.code;
  };

  const getTranslatedDescription = (fc: DoubleCheckFailureCode) => {
    if (!fc.id || translations.length === 0) return fc.description;
    const tr = translations.find(x => x.work_code_id === fc.id);
    return tr?.translated_description || fc.description;
  };

  const sortedCodes = [...failureCodes].sort((a, b) => {
    if (a.is_issue === b.is_issue) return 0;
    return a.is_issue ? 1 : -1;
  });

  return (
    <div className="space-y-3">
      <button onClick={() => setShowQuickNav(true)} className="w-full">
        <InspectionProgressBar
          items={progressItems}
          currentIndex={currentIndex}
          onItemTap={setCurrentIndex}
        />
      </button>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="bg-subtle border-b border-line px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Shield size={18} className="text-theme-secondary flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-theme-primary text-sm truncate">
                  {dc.size}" {t('dcForm.doubleCheckLabel')}
                </h3>
                <p className="text-xs text-theme-secondary truncate">{dc.description}</p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-2">
              {status === 'good' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 rounded-md text-xs font-medium">
                  <CheckCircle size={14} /> {t('status.ok')}
                </span>
              ) : status === 'issues' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 rounded-md text-xs font-medium">
                  <AlertTriangle size={14} /> {t('status.issues')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-subtle text-theme-secondary rounded-md text-xs font-medium">
                  <AlertTriangle size={14} /> {t('status.pending')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-3">
          <div className="grid grid-cols-1 gap-2">
            {sortedCodes.map(failureCode => {
              const isChecked = inspection?.failure_codes.includes(failureCode.code) || false;
              return (
                <button
                  key={failureCode.code}
                  onClick={() => handleFailureCodeToggle(dc.id, failureCode.code)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left active:scale-[0.98] ${
                    isChecked
                      ? failureCode.is_issue
                        ? 'border-red-400 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                        : 'border-emerald-400 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10'
                      : 'border-line bg-surface'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    isChecked
                      ? failureCode.is_issue ? 'border-red-500 bg-red-500' : 'border-emerald-500 bg-emerald-500'
                      : 'border-line'
                  }`}>
                    {isChecked && <CheckCircle size={14} className="text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-theme-primary block">{getTranslatedCode(failureCode)}</span>
                    <span className="text-xs text-theme-secondary block truncate">{getTranslatedDescription(failureCode)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {status === 'issues' && (
            <div className="mt-3 space-y-3">
              <div>
                <label className="block text-xs font-medium text-theme-secondary mb-1.5">
                  {t('dcForm.failureNotes')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={inspection?.notes || ''}
                  onChange={(e) => handleNotesChange(dc.id, e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-surface text-theme-primary border border-line rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder={t('dcForm.describeIssuesPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-theme-secondary mb-1.5">{t('dcForm.photosOptional')}</label>
                <PhotoCapture
                  photoUrls={inspection?.photo_urls || []}
                  bucket={PHOTO_BUCKETS.INSPECTION}
                  pathPrefix={`doublecheck_${dc.id}`}
                  onPhotosChange={(urls, geo) => handlePhotosChange(dc.id, urls, geo)}
                  photoGeo={inspection?.photo_geo}
                  localDraftId={localDraftId}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-surface border border-line rounded-xl text-theme-primary font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-subtle transition-colors"
        >
          <ChevronLeft size={20} />
          <span>{t('nav.prev')}</span>
        </button>
        <button
          onClick={() => setShowQuickNav(true)}
          className="p-3.5 bg-surface border border-line rounded-xl text-theme-secondary active:bg-subtle transition-colors"
          title={t('nav.jumpToDCTitle')}
        >
          <List size={20} />
        </button>
        <button
          onClick={goNext}
          disabled={currentIndex === doubleChecks.length - 1}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-blue-700 transition-colors"
        >
          <span>{t('nav.next')}</span>
          <ChevronRight size={20} />
        </button>
      </div>

      <InspectionQuickNav
        items={progressItems}
        currentIndex={currentIndex}
        onSelect={setCurrentIndex}
        isOpen={showQuickNav}
        onClose={() => setShowQuickNav(false)}
        title={t('nav.jumpToDC')}
      />
    </div>
  );
}
